import * as Radix from '@radix-ui/react-dropdown-menu';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { Check } from './icons';

export interface MenuAction {
  label: ReactNode;
  onSelect?: () => void;
  /** Navigate instead of calling onSelect */
  to?: string;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Render a check mark (for radio-like groups such as the theme picker) */
  checked?: boolean;
}
export type MenuItem = MenuAction | { separator: true } | { heading: string };

export interface DropdownMenuProps {
  trigger: ReactElement;
  items: MenuItem[];
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom';
  className?: string;
}

const itemClass =
  'flex w-full cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm outline-none ' +
  'data-[highlighted]:bg-gray-100 dark:data-[highlighted]:bg-gray-700 data-[disabled]:pointer-events-none data-[disabled]:opacity-50';

/** Keyboard-navigable menu on Radix. Items with `to` render as router links. */
export function DropdownMenu({ trigger, items, align = 'end', side = 'bottom', className }: DropdownMenuProps) {
  return (
    <Radix.Root modal={false}>
      <Radix.Trigger asChild>{trigger}</Radix.Trigger>
      <Radix.Portal>
        <Radix.Content
          align={align}
          side={side}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-50 min-w-[11rem] rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none dark:border-gray-700 dark:bg-gray-800',
            className,
          )}
        >
          {items.map((item, i) => {
            if ('separator' in item) return <Radix.Separator key={i} className="my-1 h-px bg-gray-200 dark:bg-gray-700" />;
            if ('heading' in item)
              return (
                <Radix.Label key={i} className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {item.heading}
                </Radix.Label>
              );
            const colour = item.danger ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-200';
            const body = (
              <>
                {item.icon && <span className="shrink-0 text-gray-400">{item.icon}</span>}
                <span className="flex-1">{item.label}</span>
                {item.checked && <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />}
              </>
            );
            if (item.to) {
              return (
                <Radix.Item key={i} asChild disabled={item.disabled} className={cn(itemClass, colour)}>
                  <Link to={item.to}>{body}</Link>
                </Radix.Item>
              );
            }
            return (
              <Radix.Item key={i} disabled={item.disabled} onSelect={() => item.onSelect?.()} className={cn(itemClass, colour)}>
                {body}
              </Radix.Item>
            );
          })}
        </Radix.Content>
      </Radix.Portal>
    </Radix.Root>
  );
}
